import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDriverControllers } from './controller';
import { DriverModel } from '../../models/driver';
import { numberFormat } from '../../utils/nunberFormat';
import { DEFAULT_ROWS_PER_PAGE, FETCH_MORE_DATA_AMOUNT } from '../../config-global';
import TableSearchNotFound from '../../components/tableSearchNotFound';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DriverForm from './form/index';
import AddIcon from '@mui/icons-material/Add';
import { SELECT_BACKGROUND_COLOR, TEXT_TITLE_COLOR } from '../../constants/colors';
import TabPanelDriverPage from './components/tabPanel';
import StatusChip from '../../components/StatusChip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import { convertRoleDriverToString } from '../../utils/convertRole';
import { IMAGE_RIDER_PROFILE } from '../../lib/endpoints/profileImage';
import { DriverEnum } from '../../enums/driverEnum';
import ChangePasswordForm from './components/changePassword';
import KeyIcon from '@mui/icons-material/Key';
import LoadingAnimatedIcon from '../../components/loading/loadingAnimated';
import TabPanelOnlineDriverPage from './components/tabOnlineRider';
import StatusOnlineChip from '../../components/statusOnlineChip';

const DriverView = () => {
  const { t } = useTranslation('global');

  const controller = useDriverControllers();

  return (
    <Box>
      {
        controller.openFormRiderUser ?
          <DriverForm onClose={controller.handleCloseForm} selectedProfileData={controller.selectedProfile} onFetchData={controller.handleFetchData} selected={controller.selected} />
          : (
            <>
              {
                controller.loadingData ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <LoadingAnimatedIcon />
                </Box> : <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography variant="h4" color={TEXT_TITLE_COLOR} fontWeight='bold'>{t("driverUser_page.Driver Management")}</Typography>
                      <Box sx={{ ml: 1 }}>
                        <TextField
                          fullWidth
                          placeholder={`${t('driverUser_page.Search by name')},${t("driverUser_page.Phone Number")}`}
                          value={controller.searchUserValue}
                          onChange={controller.handleSearchUser}
                          sx={{ maxWidth: 250 }}
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ width: 200 }}>

                        <FormControl fullWidth size='small'>
                          <InputLabel htmlFor="driver-label-id">{t("driverUser_page.Role")}</InputLabel>
                          <Select
                            labelId="driver-label-id"
                            id="driver-id"
                            label="Role driver"
                            value={controller.selectedDriverType}
                            onChange={controller.handleSelectedDriverType}
                            sx={{ bgcolor: SELECT_BACKGROUND_COLOR, '.MuiOutlinedInput-notchedOutline': { border: 0 }, borderRadius: '8px' }}
                            inputProps={{
                              name: "driver-type",
                              id: "driver-type-select",
                            }}
                          >
                            {
                              Object.values(DriverEnum).map((type: DriverEnum) => (
                                <MenuItem key={type} value={type}>
                                  {t("driverUser_page." + type)}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Box>
                  <Box mt={2} mb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 4, background: '#c7ebff', borderRadius: '15px' }}>
                          <Typography color={TEXT_TITLE_COLOR} fontWeight="bold" variant='h5'>
                            <span>
                              {t("driverUser_page.All Driver")}
                            </span>
                          </Typography>
                          <Typography color={TEXT_TITLE_COLOR} fontWeight="bold" variant='h5'>{controller?.data?.length}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 4, background: '#c1fec6', borderRadius: '15px' }}>
                          <Typography color={TEXT_TITLE_COLOR} fontWeight="bold" variant='h5'>
                            <span>
                              {t("driverUser_page.Is Online")}
                            </span>
                          </Typography>
                          <Typography color={TEXT_TITLE_COLOR} fontWeight="bold" variant='h5'>{numberFormat(controller.countIsOnlineRider)}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 4, background: '#ffacb6', borderRadius: '15px' }}>
                          <Typography color={TEXT_TITLE_COLOR} fontWeight="bold" variant='h5'>
                            <span>
                              {t("driverUser_page.Is Offline")}
                            </span>
                          </Typography>
                          <Typography color={TEXT_TITLE_COLOR} fontWeight="bold" variant='h5'>{numberFormat(controller.countIsOfflineRider)}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Button variant='contained' sx={{ textTransform: 'none', background: '#11A2F1' }} onClick={controller.handleOpenForm}>
                      <AddIcon />
                      &nbsp;&nbsp;
                      {t("driverUser_page.Create Driver User")}
                    </Button>
                  </Box>
                  <Box mt={2}>
                    <TabPanelOnlineDriverPage value={controller.tabDriverOnline} onChange={controller.handleTabRiderOnline} />
                  </Box>
                  <Box mt={1}>
                    <TabPanelDriverPage value={controller.tabDriverPage} onChange={controller.handleDriverTabChange} />
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ background: "#F4F6F8" }}>
                          <TableRow>
                            <TableCell sx={{ borderBottom: 'none' }}>{t("driverUser_page.No")}</TableCell>
                            <TableCell sx={{ borderBottom: 'none' }}>{t("driverUser_page.Profile")}</TableCell>
                            <TableCell sx={{ borderBottom: 'none' }}>{t("driverUser_page.Full Name")}</TableCell>
                            <TableCell sx={{ borderBottom: 'none' }}>{t("driverUser_page.Rider Number")}</TableCell>
                            <TableCell align='left' sx={{ borderBottom: 'none' }}>{t("driverUser_page.Phone number")}</TableCell>
                            <TableCell align='center' sx={{ borderBottom: 'none' }}>{t("driverUser_page.Role")}</TableCell>
                            <TableCell align='center' sx={{ borderBottom: 'none' }}>{t("driverUser_page.Active Status")}</TableCell>
                            <TableCell align='center' sx={{ borderBottom: 'none' }}>{t("driverUser_page.Status")}</TableCell>
                            <TableCell sx={{ borderBottom: 'none' }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            controller.rowUserRider.map((row: DriverModel, index: number) => {
                              const { profile, rider } = row;
                              const isActive: boolean = !rider?.temporaryBan
                              const isOnline: boolean = rider?.online
                              return (
                                <TableRow key={index}>
                                  <TableCell align="left" sx={{ borderBottom: 'none' }}>
                                    {numberFormat(index + 1 + DEFAULT_ROWS_PER_PAGE * (controller.currentPage - 1))}
                                  </TableCell>
                                  <TableCell align="left" sx={{ borderBottom: 'none' }}>
                                    <Avatar
                                      alt="Staff"
                                      style={{
                                        cursor: 'pointer',
                                        objectFit: 'contain',
                                        borderRadius: 180,
                                        width: 50,
                                        height: 50,
                                      }}
                                      src={IMAGE_RIDER_PROFILE + profile?.image?.url}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: 'none' }}>
                                    {profile.fullName}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: 'none' }}>
                                    {rider?.driverNo}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: 'none' }}>
                                    {profile.phoneNumber}
                                  </TableCell>
                                  <TableCell align='center' sx={{ borderBottom: 'none' }}>
                                    {t("driverUser_page." + rider?.type)}
                                  </TableCell>
                                  <TableCell align='center' sx={{ borderBottom: 'none' }}>
                                    <StatusChip status={isActive} />
                                  </TableCell>
                                  <TableCell align='center' sx={{ borderBottom: 'none' }}>
                                    <StatusOnlineChip status={isOnline} />
                                  </TableCell>
                                  <TableCell align="right" sx={{ borderBottom: 'none' }}>
                                    <IconButton
                                      size="large"
                                      color="inherit"
                                      onClick={(e: any) => controller.handleOpenMenu(e, rider?.id, profile?.id)}
                                    >
                                      <MoreHorizIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )
                            })
                          }
                        </TableBody>
                        {controller.isSearchUserNotFound && (
                          <TableSearchNotFound
                            searchValue={controller.searchUserValue}
                            colSpan={8}
                          />
                        )}
                      </Table>
                    </TableContainer>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                      <Pagination
                        color="primary"
                        page={controller.currentPage}
                        onChange={controller.handleChangePage}
                        count={controller.countPagination}
                      />
                    </Box>
                  </Box>
                </>
              }

              <Popover
                open={Boolean(controller.openMenu)}
                anchorEl={controller.openMenu}
                onClose={controller.handleCloseMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    p: 1,
                    width: 200,
                    borderRadius: '10px',
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                    },
                  },
                }}
              >
                <MenuItem onClick={() => controller.handleOpenEditRider()}>
                  <EditIcon />
                  &nbsp;&nbsp; {t("Edit")}
                </MenuItem>
                <MenuItem onClick={() => controller.handleOpenChangePasswordForm()}>
                  <KeyIcon />
                  &nbsp;&nbsp;{t("Change password")}
                </MenuItem>
                <MenuItem onClick={() => controller.handleOpenDeleteForm()}>
                  <DeleteIcon color='error' />
                  &nbsp;&nbsp; {t("Delete")}
                </MenuItem>
              </Popover>
              <ConfirmDialog
                open={controller.openDeleteForm}
                title='Do you want to delete this Data ?'
                loading={controller.loadingDeleteButton}
                onCancel={controller.handleCloseDeleteForm}
                onConfirm={() => controller.handleDeleteRiderUser()}
              />
              <ChangePasswordForm
                open={controller.openChangePassword}
                loading={controller.loadingConfirmButton}
                ownerPassword={controller.ownerPassword}
                newPassword={controller.newPassword}
                confirmNewPassword={controller.confirmNewPassword}
                errorMessage={controller.errorMessage}
                onCancel={controller.handleCloseChangePasswordForm}
                onConfirm={() => controller.handleSubmit()}
              />
            </>
          )
      }
    </Box>
  );
};
export default DriverView;